import type { FormField } from '@re/form-engine';
import type { Lookups } from '@re/form-ui';
import { Alert, Box, Chip, InputAdornment, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useMemo, useState } from 'react';
import { TextField } from '../../components/text-field';
import { isEn, nameOf, tr } from '../../lib/i18n';
import { matches } from '../../lib/search';
import { isDisplay } from './helpers';
import { dimFields, parseQuick, toggleVisible, visibleFor, type Dim } from './visibility';

interface Props {
  fields: FormField[];
  lookups?: Lookups;
  onChange: (next: FormField[]) => void;
  onOpen: (key: string) => void;
}

/**
 * Fields × request types (or categories / property types): one click decides who sees a field.
 * Writes the same `in` / `not_in` conditions the field editor writes.
 */
export function VisibilityMatrix({ fields, lookups, onChange, onOpen }: Props) {
  const [dim, setDim] = useState<Dim>('request_type');
  const [q, setQ] = useState('');
  /** property types: one category at a time ('' = all) */
  const [cat, setCat] = useState('');
  const cols = useMemo((): { code: string; label: string; group?: string; cat?: string; first?: boolean }[] => {
    if (!lookups) return [];
    if (dim === 'request_type') return lookups.requestTypes.map((r) => ({ code: r.code, label: nameOf(r) }));
    if (dim === 'property_category') return lookups.categories.map((r) => ({ code: r.code, label: nameOf(r) }));
    return lookups.categories
      .filter((c) => !cat || c.code === cat)
      .flatMap((c) => lookups.propertyTypes.filter((t) => t.category_id === c.id).map((t, i) => ({ code: t.code, label: nameOf(t), group: nameOf(c), cat: c.code, first: i === 0 })));
  }, [dim, lookups, cat]);
  const groups = useMemo(() => {
    const out: { name: string; span: number }[] = [];
    for (const c of cols) {
      const last = out[out.length - 1];
      if (last && last.name === c.group) last.span++;
      else out.push({ name: c.group ?? '', span: 1 });
    }
    return out;
  }, [cols]);

  if (!lookups) return null;
  const rows = fields
    .map((f, i) => ({ f, i, dims: dimFields(fields, i) }))
    .filter(({ f }) => !isDisplay(f.type) && f.type !== 'request_type' && matches(q, f.label.ar, f.label.en, f.key));
  const hasSource = fields.some((f) => f.type === dim);
  // property-type view: the categories each field is limited to (undefined = all)
  const catSets: Record<string, string[] | undefined> = {};
  if (dim === 'property_type') for (const { f, dims } of rows) catSets[f.key] = parseQuick(f.visibleWhen, dims, lookups)?.sets.property_category;
  const set = (i: number, f: FormField) => onChange(fields.map((x, j) => (j === i ? f : x)));
  const cellW = dim === 'property_type' ? 44 : 92;
  // `left` is flipped to `right` by the RTL style plugin, so this is the start edge in both languages
  const stickyStart = { left: 0 };

  return (
    <Stack spacing={1.5} sx={{ minHeight: 0, flex: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <ToggleButtonGroup size="small" exclusive value={dim} onChange={(_, v) => v && setDim(v)}>
          <ToggleButton value="request_type">{tr('نوع الطلب', 'Request type')}</ToggleButton>
          <ToggleButton value="property_category">{tr('فئة العقار', 'Category')}</ToggleButton>
          <ToggleButton value="property_type">{tr('نوع العقار', 'Property type')}</ToggleButton>
        </ToggleButtonGroup>
        <TextField size="small" placeholder={tr('ابحث عن حقل…', 'Search fields…')} value={q} onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 220 }}
          slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }} />
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 220 }}>
          {tr('علامة = الحقل يظهر لهذا الاختيار. اضغط على اسم الحقل لفتحه.', 'Checked = the field shows for that choice. Click a field name to open it.')}
        </Typography>
      </Stack>
      {dim === 'property_type' && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Chip label={tr(`كل الفئات (${lookups.propertyTypes.length})`, `All categories (${lookups.propertyTypes.length})`)} color={!cat ? 'primary' : 'default'} variant={!cat ? 'filled' : 'outlined'} onClick={() => setCat('')} />
          {lookups.categories.map((c) => {
            const n = lookups.propertyTypes.filter((t) => t.category_id === c.id).length;
            return <Chip key={c.code} label={`${nameOf(c)} (${n})`} color={cat === c.code ? 'primary' : 'default'} variant={cat === c.code ? 'filled' : 'outlined'} onClick={() => setCat(c.code)} />;
          })}
        </Stack>
      )}
      {!hasSource ? (
        <Alert severity="info">{tr('النموذج لا يحتوي على هذا الحقل، فلا يمكن ربط الظهور به.', 'The form has no such field, so visibility can’t depend on it.')}</Alert>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
          <Box component="table" sx={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: 13, minWidth: '100%',
            '& th, & td': { borderBottom: 1, borderColor: 'divider', p: 0 },
            '& thead': { position: 'sticky', top: 0, zIndex: 2 },
            '& thead th': { bgcolor: 'background.paper', fontWeight: 600 },
            '& tbody tr:hover td': { bgcolor: 'action.hover' },
            '& input': { width: 18, height: 18, cursor: 'pointer', accentColor: 'currentColor', color: 'text.primary', m: 0 },
            '& input:disabled': { cursor: 'not-allowed' } }}>
            <thead>
              {dim === 'property_type' && (
                <tr>
                  <Box component="th" sx={{ position: 'sticky', ...stickyStart, zIndex: 3 }} />
                  {groups.map((g, i) => (
                    <Box component="th" key={i} colSpan={g.span} sx={{ px: 1, py: 0.75, textAlign: 'start', borderInlineStart: '2px solid', borderInlineStartColor: 'text.primary', whiteSpace: 'nowrap', bgcolor: i % 2 ? 'action.hover' : 'background.paper' }}>
                      {/* the name stays in view while the group scrolls past */}
                      <Box component="span" sx={{ position: 'sticky', left: 236, px: 0.5 }}>{g.name} ({g.span})</Box>
                    </Box>
                  ))}
                </tr>
              )}
              <tr>
                <Box component="th" sx={{ position: 'sticky', ...stickyStart, zIndex: 3, minWidth: 220, px: 1.5, py: 1, textAlign: 'start' }}>{tr('الحقل', 'Field')}</Box>
                {cols.map((c) => (
                  <Box component="th" key={c.code} sx={{ width: cellW, minWidth: cellW, px: 0.5, py: 1, textAlign: 'center', verticalAlign: 'bottom', ...(c.first ? { borderInlineStart: '2px solid', borderInlineStartColor: 'text.primary' } : {}) }}>
                    {dim === 'property_type' ? (
                      <Tooltip title={c.label}><Box sx={{ writingMode: 'vertical-rl', transform: isEn() ? 'rotate(180deg)' : undefined, mx: 'auto', maxHeight: 120, overflow: 'hidden', whiteSpace: 'nowrap', fontWeight: 500 }}>{c.label}</Box></Tooltip>
                    ) : c.label}
                  </Box>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(({ f, i, dims }) => {
                const usable = !!dims[dim];
                return (
                  <tr key={f.key}>
                    <Box component="td" sx={{ position: 'sticky', ...stickyStart, bgcolor: 'background.paper', zIndex: 1, px: 1.5, py: 0.75, maxWidth: 260 }}>
                      <Box component="button" type="button" onClick={() => onOpen(f.key)} sx={{ all: 'unset', cursor: 'pointer', display: 'block', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240, '&:hover, &:focus-visible': { textDecoration: 'underline' } }}>
                        {(isEn() ? f.label.en : f.label.ar) || f.label.ar || f.key}
                        {f.required && <Box component="span" sx={{ color: 'error.main' }}> *</Box>}
                      </Box>
                    </Box>
                    {cols.map((c) => {
                      const v = usable ? visibleFor(f, dim, c.code, dims, lookups) : null;
                      // a property type whose category this field excludes never shows, whatever its own box says
                      const blocked = !!c.cat && !!catSets[f.key] && !catSets[f.key]!.includes(c.cat);
                      const why = !usable
                        ? tr('هذا الحقل قبل الحقل الذي يحدد الاختيار', 'This field comes before the field that sets the choice')
                        : v === null ? tr('مضبوط حسب الحالة (أو شرط متقدم) — عدّله من «حسب الحالة» أو محرر الحقل', 'Set by case (or an advanced rule) — edit it in “By case” or the field editor')
                          : blocked ? tr(`مخفي: الحقل لا يظهر لفئة «${c.group}» (من «فئة العقار»)`, `Hidden: the field doesn’t show for the “${c.group}” category`) : '';
                      return (
                        <Box component="td" key={c.code} sx={{ textAlign: 'center', py: 0.5, ...(c.first ? { borderInlineStart: '2px solid', borderInlineStartColor: 'text.primary' } : {}) }}>
                          <Tooltip title={why} disableHoverListener={!why}>
                            <span>
                              <input type="checkbox" aria-label={`${f.label.ar || f.key} — ${c.label}`} checked={v !== false && usable && !blocked} disabled={v === null || blocked}
                                onChange={(e) => set(i, toggleVisible(f, dim, c.code, e.target.checked, dims, lookups))} />
                            </span>
                          </Tooltip>
                        </Box>
                      );
                    })}
                  </tr>
                );
              })}
              {!rows.length && (
                <tr><Box component="td" colSpan={cols.length + 1} sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>{tr('لا توجد حقول', 'No fields')}</Box></tr>
              )}
            </tbody>
          </Box>
        </Box>
      )}
    </Stack>
  );
}
